/**
 * ModuleRunTab Component
 * 
 * Pre-flight checks and run button for module analysis.
 * Shows data gate status, required files, and starts analysis.
 * Supports backend worker flow with real-time progress for compatible modules.
 */

import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle, XCircle, AlertTriangle, Play, Loader2, Shield, FileText, Clock, StopCircle, Zap, Activity, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DataGateWarning } from '@/components/DataGateWarning';
import type { ModuleDefinition, DataGateStatus, FileKey, EngagementFile } from '@/lib/modules/types';
import { getFileKeyDisplayName } from '@/lib/modules/file-keys';

// Helper function for clearer SIE file labels
const getRequiredFileLabel = (fileKey: FileKey): string => {
  const labels: Partial<Record<FileKey, string>> = {
    'GL_SIE': 'Huvudbok innevarande år (SIE)',
    'GL_SIE_PREV': 'Huvudbok föregående år (SIE)',
  };
  return labels[fileKey] || getFileKeyDisplayName(fileKey);
};

interface BatchStats {
  queued: number;
  processing: number;
  completed: number;
  failed: number;
  total: number;
}

interface ModuleRunTabProps {
  module: ModuleDefinition;
  auditId: string;
  dataGateStatus: DataGateStatus;
  engagementFiles: Record<FileKey, EngagementFile | undefined>;
  isRunning: boolean;
  lastRunAt?: string;
  onStartAnalysis: () => Promise<void>;
  onNavigateToFiles: () => void;
  // Backend worker props (optional - for modules that support it)
  useBackendWorker?: boolean;
  batchStats?: BatchStats | null;
  progress?: number;
  workerError?: string | null;
  onCancelAnalysis?: () => Promise<void>;
  // Tax account configuration props (optional)
  showTaxAccountConfig?: boolean;
  toleranceDays?: number;
  onToleranceDaysChange?: (days: number) => void;
  companySize?: 'small' | 'large';
  onCompanySizeChange?: (size: 'small' | 'large') => void;
  vatReportingPeriod?: 'monthly' | 'quarterly';
  onVatReportingPeriodChange?: (period: 'monthly' | 'quarterly') => void;
  // Field mapping check
  hasFieldMapping?: boolean;
}

export function ModuleRunTab({
  module,
  auditId,
  dataGateStatus,
  engagementFiles,
  isRunning,
  lastRunAt,
  onStartAnalysis,
  onNavigateToFiles,
  useBackendWorker = false,
  batchStats,
  progress = 0,
  workerError,
  onCancelAnalysis,
  showTaxAccountConfig = false,
  toleranceDays = 3,
  onToleranceDaysChange,
  companySize = 'small',
  onCompanySizeChange,
  vatReportingPeriod = 'monthly',
  onVatReportingPeriodChange,
  hasFieldMapping = true,
}: ModuleRunTabProps) {
  const { t } = useTranslation();
  const [overrideDataGate, setOverrideDataGate] = useState(false);

  // Check required files
  const missingRequiredFiles = module.requiredFiles.filter(fk => !engagementFiles[fk]);
  const hasAllRequiredFiles = missingRequiredFiles.length === 0;

  // Check data gate (skip for module 0 itself)
  const needsDataGate = !module.isDataGate;
  const dataGatePassed = dataGateStatus === 'passed' || dataGateStatus === 'passed_with_warnings';
  const canRunWithoutDataGate = overrideDataGate || dataGatePassed;

  // Overall readiness
  const canRun = hasAllRequiredFiles && (canRunWithoutDataGate || !needsDataGate) && !isRunning;

  const handleStartAnalysis = async () => {
    await onStartAnalysis();
  };

  const handleCancelAnalysis = async () => {
    if (onCancelAnalysis) {
      await onCancelAnalysis();
    }
  };

  // Calculate batch progress details
  const completedBatches = batchStats?.completed || 0;
  const totalBatches = batchStats?.total || 0;
  const processingBatches = batchStats?.processing || 0;
  const failedBatches = batchStats?.failed || 0;

  return (
    <div className="space-y-6">
      {/* Backend Worker Badge */}
      {useBackendWorker && (
        <Alert className="border-primary/30 bg-primary/5">
          <Zap className="h-4 w-4 text-primary" />
          <AlertTitle className="text-primary">{t('modules.backendWorker.title')}</AlertTitle>
          <AlertDescription className="text-muted-foreground">
            {t('modules.backendWorker.description')}
          </AlertDescription>
        </Alert>
      )}

      {/* Error Alert */}
      {workerError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Fel vid analys</AlertTitle>
          <AlertDescription>{workerError}</AlertDescription>
        </Alert>
      )}

      {/* Missing Field Mapping Warning */}
      {hasAllRequiredFiles && !hasFieldMapping && (
        <Alert className="border-warning/50 bg-warning/10">
          <AlertTriangle className="h-4 w-4 text-warning" />
          <AlertTitle className="text-warning">Fältmappning saknas</AlertTitle>
          <AlertDescription>
            Gå till Filer-fliken och klicka "Mappa fält" på den uppladdade filen för att konfigurera vilka kolumner som motsvarar datum, belopp osv.
            <Button 
              variant="link" 
              size="sm" 
              className="h-auto p-0 ml-1 text-warning underline"
              onClick={onNavigateToFiles}
            >
              Gå till Filer →
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Tax Account Configuration (only for tax account module) */}
      {showTaxAccountConfig && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Analysinställningar
            </CardTitle>
            <CardDescription>
              Justera parametrar för skattekonto-analysen
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Företagsstorlek & Momsperiod */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Företagsstorlek</Label>
                <Select value={companySize} onValueChange={(v) => onCompanySizeChange?.(v as 'small' | 'large')}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">Litet företag (≤49 anst.)</SelectItem>
                    <SelectItem value="large">Stort företag (≥50 anst.)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Momsredovisning</Label>
                <Select value={vatReportingPeriod} onValueChange={(v) => onVatReportingPeriodChange?.(v as 'monthly' | 'quarterly')}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Månadsvis</SelectItem>
                    <SelectItem value="quarterly">Kvartalsvis</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Tolerance Slider */}
            <div className="space-y-4">
              <Label>
                Tolerans för sena betalningar ({toleranceDays} {toleranceDays === 1 ? 'dag' : 'dagar'})
              </Label>
              <Slider
                value={[toleranceDays]}
                onValueChange={(value) => onToleranceDaysChange?.(value[0])}
                max={7}
                min={0}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Strikt (0 dagar)</span>
                <span>Standard (3 dagar)</span>
                <span>Flexibel (7 dagar)</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Betalningar inom toleransen flaggas INTE som försenade.
                AI:n identifierar alla avvikelser, men de inom toleransen filtreras bort.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pre-flight Checks */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Förutsättningar
          </CardTitle>
          <CardDescription>
            Kontrollera att alla krav är uppfyllda innan analysen startas
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Required Files Check */}
          <div className="flex items-start gap-3 p-3 rounded-lg border">
            {hasAllRequiredFiles ? (
              <CheckCircle className="h-5 w-5 text-success mt-0.5" />
            ) : (
              <XCircle className="h-5 w-5 text-destructive mt-0.5" />
            )}
            <div className="flex-1">
              <div className="font-medium text-sm">Obligatoriska filer</div>
              {hasAllRequiredFiles ? (
                <p className="text-xs text-muted-foreground">
                  Alla {module.requiredFiles.length} obligatoriska filer är uppladdade
                </p>
              ) : (
                <div className="space-y-1 mt-1">
                  <p className="text-xs text-destructive">
                    {missingRequiredFiles.length} fil(er) saknas:
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {missingRequiredFiles.map(fk => (
                      <Badge key={fk} variant="outline" className="text-xs">
                        {getRequiredFileLabel(fk)}
                      </Badge>
                    ))}
                  </div>
                  <Button 
                    variant="link" 
                    size="sm" 
                    className="h-auto p-0 text-xs"
                    onClick={onNavigateToFiles}
                  >
                    Gå till Filer-fliken →
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Data Gate Check (only for non-data-gate modules) */}
          {needsDataGate && (
            <div className="flex items-start gap-3 p-3 rounded-lg border">
              {dataGatePassed ? (
                <CheckCircle className="h-5 w-5 text-success mt-0.5" />
              ) : overrideDataGate ? (
                <AlertTriangle className="h-5 w-5 text-warning mt-0.5" />
              ) : (
                <XCircle className="h-5 w-5 text-destructive mt-0.5" />
              )}
              <div className="flex-1">
                <div className="font-medium text-sm">Datakvalitetskontroll (Modul 0)</div>
                {dataGatePassed ? (
                  <p className="text-xs text-muted-foreground">
                    Datakvalitetskontrollen är godkänd
                    {dataGateStatus === 'passed_with_warnings' && ' (med varningar)'}
                  </p>
                ) : (
                  <div className="space-y-2 mt-1">
                    <p className="text-xs text-muted-foreground">
                      Modul 0 (Datakvalitet) bör köras först för att säkerställa datakvalitet
                    </p>
                    {!overrideDataGate && (
                      <DataGateWarning 
                        status={dataGateStatus}
                        onOverride={() => setOverrideDataGate(true)}
                      />
                    )}
                    {overrideDataGate && (
                      <Badge variant="outline" className="bg-warning/10 text-warning border-warning/20">
                        Datakvalitetskontroll överhoppad
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Last Run Info */}
          {lastRunAt && (
            <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
              <Clock className="h-5 w-5 text-muted-foreground" />
              <div>
                <div className="font-medium text-sm">Senaste körning</div>
                <p className="text-xs text-muted-foreground">
                  {new Date(lastRunAt).toLocaleString('sv-SE')}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Run Button / Progress */}
      <Card className={canRun && !isRunning ? 'border-primary/50 bg-primary/5' : ''}>
        <CardContent className="pt-6">
          <div className="flex flex-col items-center gap-4 py-4">
            {isRunning ? (
              <>
                {useBackendWorker ? (
                  <>
                    <Activity className="h-12 w-12 text-primary animate-pulse" />
                    <div className="text-center w-full max-w-md">
                      <p className="font-medium">Backend Worker bearbetar...</p>
                      <p className="text-sm text-muted-foreground mb-4">
                        {processingBatches > 0 
                          ? `${completedBatches} av ${totalBatches} batchar klara (${processingBatches} bearbetas)`
                          : `${completedBatches} av ${totalBatches} batchar klara`
                        }
                        {failedBatches > 0 && ` • ${failedBatches} misslyckade`}
                      </p>
                      
                      {/* Progress bar */}
                      <Progress value={progress} className="w-full h-3 mb-2" />
                      <p className="text-xs text-muted-foreground">{Math.round(progress)}% färdigt</p>
                      
                      {/* Batch stats breakdown */}
                      {batchStats && totalBatches > 0 && (
                        <div className="flex justify-center gap-4 mt-4 text-xs">
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-muted-foreground" />
                            <span>Kö: {batchStats.queued}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                            <span>Pågår: {batchStats.processing}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-success" />
                            <span>Klara: {batchStats.completed}</span>
                          </div>
                          {batchStats.failed > 0 && (
                            <div className="flex items-center gap-1">
                              <div className="w-2 h-2 rounded-full bg-destructive" />
                              <span>Fel: {batchStats.failed}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Cancel button */}
                    {onCancelAnalysis && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleCancelAnalysis}
                        className="gap-2 mt-2"
                      >
                        <StopCircle className="h-4 w-4" />
                        Avbryt analys
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    <Loader2 className="h-12 w-12 text-primary animate-spin" />
                    <div className="text-center">
                      <p className="font-medium">Analys pågår...</p>
                      <p className="text-sm text-muted-foreground">
                        Detta kan ta några minuter beroende på datamängd
                      </p>
                    </div>
                    <Progress value={33} className="w-full max-w-xs" />
                  </>
                )}
              </>
            ) : (
              <>
                <Play className={`h-12 w-12 ${canRun ? 'text-primary' : 'text-muted-foreground'}`} />
                <div className="text-center">
                  <p className="font-medium">
                    {canRun ? 'Redo att köra analys' : 'Uppfyll kraven ovan för att köra'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {module.shortDescription}
                  </p>
                  {useBackendWorker && (
                    <Badge variant="outline" className="mt-2 bg-primary/10 text-primary border-primary/20">
                      <Zap className="h-3 w-3 mr-1" />
                      Backend Worker
                    </Badge>
                  )}
                </div>
                <Button 
                  size="lg" 
                  disabled={!canRun}
                  onClick={handleStartAnalysis}
                  className="gap-2"
                >
                  <Play className="h-4 w-4" />
                  Starta analys
                </Button>
                {overrideDataGate && !dataGatePassed && (
                  <p className="text-xs text-warning text-center max-w-sm">
                    ⚠️ Analysen körs utan datakvalitetskontroll
                  </p>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
